<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useFetchMedicalSchemeHealthSeekers
} from '@/hooks/use-health-insurances';
import { useHealthSeekersStore } from '@/store/use-health-seekers-store';
import { useMedicalSchemesStore } from '@/store/use-medical-schemes-store';
import { useTariffCodeStore } from '@/store/use-tariff-codes-store';
import { parseHealthSeekerData } from '@/utils/utils';
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Building2,
  Calendar,
  Eye,
  FileText,
  Search,
  SettingsIcon,
  Users
} from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';



// Get store
const tariffCodesStore = useTariffCodeStore();
const medicalSchemeStore = useMedicalSchemesStore();
const healthSeekersStore = useHealthSeekersStore();

// Destructure state with storeToRefs
const { medicalScheme } = storeToRefs(medicalSchemeStore);

// Get route to access params
const route = useRoute();

// Router
const router = useRouter();

// Props
const props = defineProps<{
  schemeId?: string
}>();

// Use schemeId from props or route params [TODO: Can get Scheme-Id from the medicalSchemeStore]
const schemeId = computed(() => props.schemeId || route.params.schemeId as string);

// Filters
const searchQuery = ref('');
const selectedFunder = ref('');
const selectedStatus = ref('');
const selectedYear = ref('');
const selectedClient = ref<any>(null);

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(15);
const currentYear = new Date().getFullYear();
const previousYear = currentYear - 1;

// Fetch clients data
const {
  data: fetchedData,
  isLoading, 
  isError, 
  error, 
  refetch 
} = useFetchMedicalSchemeHealthSeekers(schemeId.value, null)

// Use computed to reactively access the data
const clients = computed(() => fetchedData.value || [])

// Filtered clients
const filteredClients = computed(() => {
  let filtered = clients.value

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter((item: any) =>
      item.client.first_name.toLowerCase().includes(query) ||
      item.client.last_name.toLowerCase().includes(query) ||
      item.membership_number.includes(query) ||
      item.client.email?.toLowerCase().includes(query) ||
      item.client.id_number?.includes(query) ||
      item.funder.name.toLowerCase().includes(query)
    )
  }

  // Funder filter
  if (selectedFunder.value && selectedFunder.value !== 'all') {
    filtered = filtered.filter((item: any) => item.funder.funder.name === selectedFunder.value)
  }

  // Status filter
  if (selectedStatus.value && selectedStatus.value !== 'all') {
    const isActive = selectedStatus.value === 'active'
    filtered = filtered.filter((item: any) => item.is_active === isActive)
  }

  // Year filter
  if (selectedYear.value && selectedYear.value !== 'all') {
    filtered = filtered.filter((item: any) => item.date_enrolled.startsWith(selectedYear.value))
  }

  return filtered
})

// Unique funders
const uniqueFunders = computed(() => {
  const funders = clients.value.map((c: any) => c.funder.funder.name)
  return [...new Set(funders)]
})

// Unique years
const uniqueYears = computed(() => {
  const years = clients.value.map((c: any) => c.date_enrolled.substring(0, 4))
  return [...new Set(years)].sort((a, b) => parseInt(b) - parseInt(a))
})

// Stats
const stats = computed(() => ({
  total: filteredClients.value.length,
  active: filteredClients.value.filter((c: any) => c.is_active).length,
  enrolled2024: filteredClients.value.filter((c: any) => c.date_enrolled.startsWith('2024')).length,
  funders: uniqueFunders.value.length
}))

// Pagination
const totalPages = computed(() => {
  return Math.ceil(filteredClients.value.length / itemsPerPage.value)
})

const startIndex = computed(() => {
  return (currentPage.value - 1) * itemsPerPage.value
})

const endIndex = computed(() => {
  return startIndex.value + itemsPerPage.value
})

const paginatedClients = computed(() => {
  return filteredClients.value.slice(startIndex.value, endIndex.value)
})

const displayPages = computed(() => {
  const pages = []
  const maxPagesToShow = 5
  let startPage = Math.max(1, currentPage.value - Math.floor(maxPagesToShow / 2))
  let endPage = Math.min(totalPages.value, startPage + maxPagesToShow - 1)

  if (endPage - startPage < maxPagesToShow - 1) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1)
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i)
  }

  return pages
})

// Format date
const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// Get status color
const getStatusColor = (isActive: boolean) => {
  return isActive ? 'bg-green-500' : 'bg-red-500'
}

const handleFetchTariffRates = (applicableYear:number) => {
  tariffCodesStore.setApplicableYear(applicableYear.toString());
  console.log(`Scheme-Id: ${schemeId.value}`);
  console.log(`Applicable Year: ${tariffCodesStore.applicableYear}`);
  router.push(`/scheme-tariff-rates-dashboard/${schemeId.value}`)
}

const resetFilters = () => {
  searchQuery.value = ''
  selectedFunder.value = 'all'
  selectedStatus.value = 'all'
  selectedYear.value = 'all'
  currentPage.value = 1
}

const viewDetails = (client: any) => {
  selectedClient.value = client
}

const handleViewTreatmentPlan = (client: any) => {
  const healthSeeker = parseHealthSeekerData(client);

  console.log('Manage Treatment Plan for client:', healthSeeker);
  // Update Health Seeker Store
  healthSeekersStore.setHealthSeeker(healthSeeker)
  router.push(`/treatment-plan`);
}

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

const getInitials = (client: any) => {
  const first = client.first_name?.[0] || ''
  const last = client.last_name?.[0] || ''
  return `${first}${last}`.toUpperCase()
}

// Get title text
const getTitleText = (titleId: number) => {
  const titles: Record<number, string> = {
    1: 'Mr',
    2: 'Mrs',
    3: 'Ms',
    4: 'Dr',
    5: 'Prof'
  }
  return titles[titleId] || ''
}

// Get sex text
const getSexText = (sexId: number) => {
  const sexes: Record<number, string> = {
    1: 'Male',
    2: 'Female',
    3: 'Other'
  }
  return sexes[sexId] || ''
}

const handleAdHocClicked = () => {
  router.push(
    {name: 'programmePhases', 
    params: { schemeId: schemeId.value }
  });
}
// Watch for data changes
watch(fetchedData, (newData) => {
  console.log('Clients data changed:', newData)
})

</script>

<template>
  <div class="space-y-6 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">{{ medicalScheme?.name }} Members Management</h1>
        <p class="text-gray-600 mt-1">View and manage {{ medicalScheme?.name }} enrolled members</p>
      </div>
      <div>
        <Button
          @click="router.back()"
          class="flex items-center border border-gray-300 bg-gray-200 hover:bg-sky-700 hover:text-white"
        >
          <ArrowLeft /> Back
        </Button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto mb-4"></div>
        <p class="text-gray-600">Loading clients...</p>
      </div>
    </div>

    <!-- Error State -->
    <Card v-else-if="isError" class="border-red-200 bg-red-50">
      <CardContent class="flex flex-col items-center justify-center py-12">
        <AlertCircle class="h-12 w-12 text-red-600 mb-4" />
        <p class="text-red-600 font-semibold mb-2">Error loading clients</p>
        <p class="text-red-500 text-sm">{{ error?.message }}</p>
        <Button 
          @click="refetch()" 
          variant="outline"
          class="mt-4 border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
        >
          Retry
        </Button>
      </CardContent>
    </Card>

    <!-- Main Content -->
    <template v-else-if="clients.length > 0">
      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent class="pt-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Total Clients</p>
                <p class="text-3xl font-bold text-gray-900 mt-2">{{ stats.total }}</p>
              </div>
              <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users class="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent class="pt-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Active Clients</p>
                <p class="text-3xl font-bold text-green-600 mt-2">{{ stats.active }}</p>
              </div>
              <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Activity class="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent class="pt-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Enrolled 2024</p>
                <p class="text-3xl font-bold text-gray-900 mt-2">{{ stats.enrolled2024 }}</p>
              </div>
              <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Calendar class="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent class="pt-6">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-gray-600">Funders</p>
                <p class="text-3xl font-bold text-gray-900 mt-2">{{ stats.funders }}</p>
              </div>
              <div class="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Building2 class="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      
      <!-- Action Buttons -->
      <div class="flex w-full items-center justify-between border-t pt-4">
        <div>
          <h1 class="text-2xl">Members List</h1>
        </div>
        <div class="flex gap-2">
        <Button
          @click="handleFetchTariffRates(previousYear)"
          class="flex gap-2 border bg-transparent border-sky-700 text-sky-800 hover:bg-sky-800 hover:text-white"
        >
         <span class="bg-sky-700 px-2 text-white rounded-md">{{ previousYear }}</span> | Tariff Rates
        </Button>
        <Button
          @click="handleFetchTariffRates(currentYear)"
          class="flex gap-2 border bg-transparent text-green-700 border-green-600 hover:bg-green-700 hover:text-white"
        >
         <span class="border-green-600 bg-green-600 text-white px-2 rounded-md">{{ currentYear }}</span> | Tariff Rates
        </Button>
        
        <Button 
          @click="handleAdHocClicked"
          variant="outline" 
          class="gap-1 border-yellow-500 cursor-pointer hover:bg-yellow-500 hover:text-white"
        >
          <SettingsIcon class="h-4 w-4" />
          AD HOC
        </Button>
        </div>
      </div>
      
      <!-- Table -->
      <Card>
        <CardContent class="p-0">
          <!-- Filters -->
          <div class="grid grid-cols-1 md:grid-cols-5 gap-4 px-2 py-4 border-b">
            <div class="relative">
              <Search class="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                v-model="searchQuery"
                placeholder="Search clients..."
                class="pl-9"
              />
            </div>

            <Select v-model="selectedFunder">
              <SelectTrigger>
                <SelectValue placeholder="All Funders" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Funders</SelectItem>
                <SelectItem v-for="funder in uniqueFunders" :key="funder" :value="funder">
                  {{ funder }}
                </SelectItem>
              </SelectContent>
            </Select>

            <Select v-model="selectedYear">
              <SelectTrigger>
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                <SelectItem v-for="year in uniqueYears" :key="year" :value="year">
                  {{ year }}
                </SelectItem>
              </SelectContent>
            </Select>

            <Select v-model="selectedStatus">
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <Button @click="resetFilters" variant="outline" class="w-full">
              Reset Filters
            </Button>
          </div>
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-gray-50 border-b">
                <tr>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Member</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Membership</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Funder</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Option</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Date Enrolled</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr
                  v-for="item in paginatedClients"
                  :key="item.id"
                  class="hover:bg-gray-50 transition-colors"
                >
                  <!-- Client -->
                  <td class="px-6 py-4">
                    <div class="flex items-center gap-3">
                      <div class="w-10 h-10 rounded-full bg-gradient-to-br from-sky-700 to-sky-900 flex items-center justify-center text-white font-semibold text-sm">
                        {{ getInitials(item.client) }}
                      </div>
                      <div>
                        <div class="font-medium text-gray-900">
                          {{ getTitleText(item.client.title) }} {{ item.client.first_name }} {{ item.client.last_name }}
                        </div>
                        <div class="text-sm text-gray-600">ID: {{ item.client.id_number || item.client.passport_number || '-' }}</div>
                      </div>
                    </div>
                  </td>
                  <!-- Membership -->
                  <td class="px-6 py-4">
                    <div>
                      <div class="font-mono text-sm font-medium text-gray-900">{{ item.membership_number }}</div>
                      <div class="text-sm text-gray-600">Dep: {{ item.dependant_key }}</div>
                    </div>
                  </td>

                  <!-- Funder -->
                  <td class="px-6 py-4">
                    <div>
                      <div class="font-medium text-gray-900">{{ item.funder.funder.name }}</div>
                      <Badge variant="outline" class="font-mono text-xs border border-yellow-700 mt-1 py-0">
                        {{ item.funder.funder.scheme_code }}
                      </Badge>
                    </div>
                  </td>

                  <!-- Option -->
                  <td class="px-6 py-4">
                    <div>
                      <div class="font-medium text-gray-900">{{ item.funder.code }}</div>
                      <div class="text-sm text-gray-600">{{ item.funder.name }}</div>
                    </div>
                  </td>

                  <!-- Date Enrolled -->
                  <td class="px-6 py-4">
                    <div class="text-sm text-gray-900">{{ formatDate(item.date_enrolled) }}</div>
                    <div v-if="item.date_joined" class="text-sm text-gray-600">Joined: {{ formatDate(item.date_joined) }}</div>
                  </td>

                  <!-- Status -->
                  <td class="px-6 py-4">
                    <div class="flex items-center gap-2">
                      <div :class="['w-2 h-2 rounded-full', getStatusColor(item.is_active)]"></div>
                      <span class="text-sm font-medium">{{ item.is_active ? 'Active' : 'Inactive' }}</span>
                    </div>
                  </td>

                  <!-- Actions -->
                  <td class="px-6 py-4">
                    <div class="flex items-center gap-2">
                      <Dialog>
                        <DialogTrigger as-child>
                          <Button 
                            class="border border-[#b69831]"
                            variant="ghost" 
                            size="sm"
                            @click="viewDetails(item)"
                            title="View Details"
                          >
                            <Eye class="h-4 w-4" /> View
                            
                          </Button>
                        </DialogTrigger>
                      <DialogContent class="bg-slate-100 w-[70vw] max-w-none sm:max-w-none max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Client Details</DialogTitle>
                          <DialogDescription>
                            Complete information for {{ selectedClient?.client?.first_name }} {{ selectedClient?.client?.last_name }}
                          </DialogDescription>
                        </DialogHeader>

                        <div v-if="selectedClient" class="space-y-6 mt-4">
                          <!-- Personal Information -->
                          <div>
                            <h4 class="text-lg font-semibold mb-3">Personal Information</h4>
                            <div class="grid grid-cols-3 gap-4">
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Full Name</p>
                                <p class="font-semibold text-gray-900">
                                  {{ getTitleText(selectedClient?.client?.title) }} 
                                  {{ selectedClient?.client?.first_name }} 
                                  {{ selectedClient?.client?.middle_name || '' }}
                                  {{ selectedClient?.client?.last_name }}
                                </p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">ID Number</p>
                                <p class="font-semibold font-mono text-gray-900">{{ selectedClient?.client?.id_number || '-' }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Passport</p>
                                <p class="font-semibold text-gray-900">{{ selectedClient?.client?.passport_number || '-' }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Date of Birth</p>
                                <p class="font-semibold text-gray-900">{{ formatDate(selectedClient?.client?.date_of_birth) || '-' }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Sex</p>
                                <p class="font-semibold text-gray-900">{{ getSexText(selectedClient?.client?.sex) || '-' }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Language</p>
                                <p class="font-semibold text-gray-900">{{ selectedClient?.client?.language?.language || '-' }}</p>
                              </div>
                            </div>
                          </div>

                          <!-- Contact Information -->
                          <div>
                            <h4 class="text-lg font-semibold mb-3">Contact Information</h4>
                            <div class="grid grid-cols-2 gap-4">
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Email</p>
                                <p class="font-semibold text-gray-900">{{ selectedClient?.client?.email || '-' }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Mobile</p>
                                <p class="font-semibold text-gray-900">{{ selectedClient?.client?.mobile_number || '-' }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg col-span-2">
                                <p class="text-sm text-gray-600">Address</p>
                                <p class="font-semibold text-gray-900">
                                  {{ [
                                    selectedClient?.client?.unit_number,
                                    selectedClient?.client?.building_name,
                                    selectedClient?.client?.street_number,
                                    selectedClient?.client?.street_name,
                                    selectedClient?.client?.suburb,
                                    selectedClient?.client?.city,
                                    selectedClient?.client?.postal_code
                                  ].filter(Boolean).join(', ') || '-' }}
                                </p>
                              </div>
                            </div>
                          </div>

                          <!-- Membership Details -->
                          <div>
                            <h4 class="text-lg font-semibold mb-3">Membership Details</h4>
                            <div class="grid grid-cols-3 gap-4">
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Membership Number</p>
                                <p class="font-semibold font-mono text-gray-900">{{ selectedClient.membership_number }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Dependant Key</p>
                                <p class="font-semibold text-gray-900">{{ selectedClient.dependant_key }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Identifier</p>
                                <p class="font-semibold text-gray-900">{{ selectedClient.identifier }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Date Enrolled</p>
                                <p class="font-semibold text-gray-900">{{ formatDate(selectedClient.date_enrolled) }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Date Joined</p>
                                <p class="font-semibold text-gray-900">{{ formatDate(selectedClient.date_joined) }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Status</p>
                                <Badge :variant="selectedClient.is_active ? 'default' : 'destructive'">
                                  {{ selectedClient.is_active ? 'Active' : 'Inactive' }}
                                </Badge>
                              </div>
                            </div>
                          </div>

                          <!-- Funder Information -->
                          <div>
                            <h4 class="text-lg font-semibold mb-3">Funder Information</h4>
                            <div class="grid grid-cols-2 gap-4">
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Funder Name</p>
                                <p class="font-semibold text-gray-900">{{ selectedClient.funder.funder.name }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Scheme Code</p>
                                <p class="font-semibold font-mono text-gray-900">{{ selectedClient.funder.funder.scheme_code }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Option Code</p>
                                <p class="font-semibold text-gray-900">{{ selectedClient.funder.code }}</p>
                              </div>
                              <div class="p-3 bg-white rounded-lg">
                                <p class="text-sm text-gray-600">Option Name</p>
                                <p class="font-semibold text-gray-900">{{ selectedClient.funder.name }}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Button 
                      variant="default" 
                      size="sm"
                      @click="handleViewTreatmentPlan(item)"
                      title="Manage Treatment Plan"
                      class="ml-2 border border-[#b69831]"
                    >
                      <FileText class="h-4 w-4 mr-1" />
                      Treatment Plan
                    </Button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Empty State -->
            <div v-if="paginatedClients.length === 0" class="text-center py-12">
              <AlertCircle class="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <p class="text-gray-600 text-lg font-medium">No clients found</p>
              <p class="text-gray-500 text-sm mt-1">Try adjusting your filters</p>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="filteredClients.length > 0" class="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div class="text-sm text-gray-700">
              Showing <span class="font-medium">{{ startIndex + 1 }}</span> to
              <span class="font-medium">{{ Math.min(endIndex, filteredClients.length) }}</span> of
              <span class="font-medium">{{ filteredClients.length }}</span> results
            </div>
            <div class="flex gap-2">
              <Button
                @click="previousPage"
                :disabled="currentPage === 1"
                variant="outline"
                size="sm"
              >
                Previous
              </Button>
              <Button
                v-for="page in displayPages"
                :key="page"
                @click="currentPage = page"
                :variant="currentPage === page ? 'default' : 'outline'"
                size="sm"
              >
                {{ page }}
              </Button>
              <Button
                @click="nextPage"
                :disabled="currentPage === totalPages"
                variant="outline"
                size="sm"
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </template>

    <!-- Empty State -->
    <Card v-else>
      <CardContent class="flex flex-col items-center justify-center py-12">
        <AlertCircle class="h-12 w-12 text-gray-400 mb-4" />
        <p class="text-gray-500">No clients data available</p>
        <Button 
          @click="refetch()" 
          variant="outline"
          class="mt-4"
        >
          Refresh
        </Button>
      </CardContent>
    </Card>
  </div>
</template>